/**
 * Paying fines (M5 spec §12.4, §18 flow 2; seam S-M2-14; plan C14): "Pay fines" in the comms of any station of
 * law.json `fines.faction` (the Authority) within `fines.rangeM` (10 km) of its hull (scale SC-5e2: the bounding
 * sphere, `|p − centre| − R`), even when docking is refused, and in the Registry panel (C18 draws both). It clears **every** bounty for `fines.mult × Σ bounty`:
 *
 * 1. the total from the mirrored bounties (`fineTotal`);
 * 2. `CareerPort.spend(total, 'fines')` (false = not enough credits: nothing changes);
 * 3. `command({ type: 'payFines' })`; the worker clears the bounties, books the Authority's income and returns the
 *    amount. A worker amount above what was paid (a crime raced the payment) is spent too when the wallet allows;
 *    a smaller one is refunded.
 *
 * Message rate.
 */
import lawJson from '../data/strategic/law.json'
import type { CareerPort } from '../reach/ports'
import type { StrategicClient } from '../strategic/client'
import { FACTION, FACTION_IDS, type CommandResult, type FactionId, type RelationsView } from '../strategic/types'
import type { LawWorld } from './crimeDetector'

export interface FinesTuning {
  faction: FactionId
  mult: number
  rangeM: number
}

export function defaultFinesTuning(): FinesTuning {
  return { faction: lawJson.fines.faction as FactionId, mult: lawJson.fines.mult, rangeM: lawJson.fines.rangeM }
}

/** `mult × Σ bounty` over the mirrored bounties (0 = nothing owed). */
export function fineTotal(view: RelationsView | null, mult: number = lawJson.fines.mult): number {
  if (view === null) return 0
  let sum = 0
  for (let f = 0; f < FACTION_IDS.length; f++) sum += view.player.bounties[FACTION_IDS[f]] ?? 0
  return sum * mult
}

/**
 * True when a station of the fines faction (not disabled) has the point within `rangeM` of its hull (`|p − centre| − R`,
 * R = `LawWorld.sr`; scale SC-5e2). Allocation-free.
 */
export function finesStationInRange(w: LawWorld, x: number, y: number, z: number, tuning: FinesTuning = defaultFinesTuning()): boolean {
  const f = FACTION[tuning.faction]
  for (let i = 0; i < w.stations; i++) {
    if (w.stationOwner[i] !== f || w.stationDisabled[i] === 1) continue
    const dx = w.sx[i] - x
    const dy = w.sy[i] - y
    const dz = w.sz[i] - z
    const reach = tuning.rangeM + w.sr[i]
    if (dx * dx + dy * dy + dz * dz <= reach * reach) return true
  }
  return false
}

export type PayFinesResult =
  | { status: 'paid'; amount: number }
  | { status: 'nothingOwed' }
  | { status: 'notEnoughCredits'; amount: number }
  | { status: 'failed'; error: string }

export interface PayFinesDeps {
  career: Pick<CareerPort, 'spend' | 'earn'>
  client: Pick<StrategicClient, 'command'>
  view: RelationsView | null
  mult?: number
}

/** Runs the fines flow (see the file header). */
export async function payFines(deps: PayFinesDeps): Promise<PayFinesResult> {
  const total = Math.round(fineTotal(deps.view, deps.mult))
  if (total <= 0) return { status: 'nothingOwed' }
  if (!deps.career.spend(total, 'fines')) return { status: 'notEnoughCredits', amount: total }
  let r: CommandResult
  try {
    r = await deps.client.command({ type: 'payFines' })
  } catch (err) {
    deps.career.earn(total, 'fines refund')
    return { status: 'failed', error: err instanceof Error ? err.message : String(err) }
  }
  if (!r.ok) {
    deps.career.earn(total, 'fines refund')
    return { status: 'failed', error: r.error }
  }
  const owed = Math.round(r.amount ?? total)
  if (owed < total) deps.career.earn(total - owed, 'fines refund')
  else if (owed > total && deps.career.spend(owed - total, 'fines')) return { status: 'paid', amount: owed }
  return { status: 'paid', amount: owed < total ? owed : total }
}

/** Clears the mirrored bounties and wanted levels after a payment (the worker already cleared its own). */
export function clearMirroredBounties(view: RelationsView | null): void {
  if (view === null) return
  for (let f = 0; f < FACTION_IDS.length; f++) {
    const id = FACTION_IDS[f]
    if (view.player.bounties[id] !== undefined) view.player.bounties[id] = 0
    if (view.player.wanted[id] !== undefined) view.player.wanted[id] = 0
  }
}
